import { isEmpty } from 'lodash-es';
import { addDays, parseJSON } from 'date-fns';
import type { UserInfo } from '@/types/auth';

const AuthCookieNames = Object.freeze({
    SessionDuration: 'sessionDuration',
    AccessToken: 'accessToken',
    AccessTokenExpiration: 'accessTokenExpiration',
    RefreshToken: 'refreshToken',
    UserInfo: 'userInfo'
});

const usePrivateState = defineStore('authPrivateStateStore', () => {
    const sessionDuration = ref<Date>();
    const accessToken = ref<string>();
    const accessTokenExpiration = ref<Date>();
    const refreshToken = ref<string>();
    const userInfo = ref<UserInfo>();

    return { accessToken, accessTokenExpiration, refreshToken, userInfo, sessionDuration };
});

export const useUserSessionStore = defineStore('authUserSessionStore', () => {
    const options = useRuntimeConfig().public.auth;
    const app = useNuxtApp();

    // creating private state
    // https://masteringpinia.com/blog/how-to-create-private-state-in-stores
    const privateState = usePrivateState();

    const accessToken = computed(() => privateState.accessToken);
    const accessTokenExpiration = computed(() => privateState.accessTokenExpiration);
    const refreshToken = computed(() => privateState.refreshToken);
    const userInfo = computed(() => privateState.userInfo);

    const isAuthenticated = computed(() => accessToken.value !== undefined);
    const userId = computed(() =>
        isAuthenticated.value && userInfo.value !== undefined ? Number(userInfo.value.sub) : undefined
    );
    const hasLocalAccount = computed(
        () => isAuthenticated.value && userInfo.value !== undefined && userInfo.value.has_local_account
    );
    const isLoggedInAsAnotherUser = computed(
        () => isAuthenticated.value && userInfo.value !== undefined && userInfo.value.admin_username !== undefined
    );

    function setTokens(
        newAccessToken: string,
        newRefreshToken: string | undefined,
        expiresInSeconds: number,
        rememberMe?: boolean
    ) {
        if (rememberMe) {
            privateState.sessionDuration = addDays(new Date(), options.rememberMeDuration);
        }

        privateState.accessToken = newAccessToken;
        privateState.accessTokenExpiration = new Date(Date.now() + expiresInSeconds * 1000);
        privateState.refreshToken = newRefreshToken;

        writeCookies();
    }

    function setUserInfo(val?: UserInfo) {
        privateState.userInfo = !isEmpty(val) ? val : undefined;

        writeCookies();
    }

    function isInRole(roles: string | string[]) {
        if (!userInfo.value) {
            return false;
        }

        const rolesToMatch = typeof roles === 'string' ? [roles] : roles;

        return userInfo.value.role.some((r) => rolesToMatch.includes(r));
    }

    function hasAccessTokenExpired() {
        return accessTokenExpiration.value !== undefined && accessTokenExpiration.value.getTime() <= Date.now();
    }

    function clear() {
        privateState.accessToken = undefined;
        privateState.accessTokenExpiration = undefined;
        privateState.refreshToken = undefined;
        privateState.userInfo = undefined;
        privateState.sessionDuration = undefined;

        writeCookies();
    }

    function parseDate(val: string): Date | undefined {
        const date = parseJSON(val);

        return date instanceof Date && !isNaN(date.valueOf()) ? date : undefined;
    }

    // Read tokens from cookies on initial page load or page refresh
    function init() {
        privateState.sessionDuration = readCookie<Date>(app, AuthCookieNames.SessionDuration, parseDate);

        privateState.accessToken = readCookie<string>(app, AuthCookieNames.AccessToken);
        privateState.accessTokenExpiration = readCookie<Date>(app, AuthCookieNames.AccessTokenExpiration, parseDate);
        privateState.refreshToken = readCookie<string>(app, AuthCookieNames.RefreshToken);
        privateState.userInfo = readCookie<UserInfo>(app, AuthCookieNames.UserInfo);
    }

    function writeCookies() {
        writeCookie(app, AuthCookieNames.SessionDuration, privateState.sessionDuration, {
            expires: addDays(new Date(), options.rememberMeDuration)
        });
        writeCookie(app, AuthCookieNames.AccessToken, privateState.accessToken, {
            expires: privateState.sessionDuration
        });
        writeCookie(app, AuthCookieNames.AccessTokenExpiration, privateState.accessTokenExpiration, {
            expires: privateState.sessionDuration
        });
        writeCookie(app, AuthCookieNames.RefreshToken, privateState.refreshToken, {
            expires: privateState.sessionDuration
        });
        writeCookie(app, AuthCookieNames.UserInfo, privateState.userInfo, { expires: privateState.sessionDuration });
    }

    return {
        accessToken,
        accessTokenExpiration,
        refreshToken,
        userInfo,
        isAuthenticated,
        userId,
        hasLocalAccount,
        isLoggedInAsAnotherUser,
        hasAccessTokenExpired,
        setTokens,
        setUserInfo,
        isInRole,
        clear,
        init
    };
});
